import React from 'react';
import { ArchiveBoxIcon } from '@heroicons/react/24/outline';
import {
  formatJurisdictionLabel,
  type TaskTemplateListItem,
} from './templateManagementService';

interface TemplateCardProps {
  template: TaskTemplateListItem;
  onViewDetails: (templateId: string) => void;
  onCreateVariant: (templateId: string) => void;
  onEditTemplate: (templateId: string) => void;
  onArchiveTemplate?: (template: TaskTemplateListItem) => void;
  isForking?: boolean;
}

function confidenceColor(confidence?: string): string {
  if (confidence === 'high') return 'bg-emerald-500';
  if (confidence === 'medium') return 'bg-amber-500';
  if (confidence === 'low') return 'bg-rose-500';
  return 'bg-gray-400';
}

function formatDate(value?: string): string {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString();
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onViewDetails,
  onCreateVariant,
  onEditTemplate,
  onArchiveTemplate,
  isForking = false,
}) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{template.name}</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{template.description || 'No description provided.'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {template.case_subtype && (
            <span className="inline-flex items-center rounded-full bg-violet-100 px-2 py-1 text-xs font-semibold text-violet-800 dark:bg-violet-900/30 dark:text-violet-200">
              {template.case_subtype.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
          )}
          {template.template_kind && template.template_kind !== 'base' && (
            <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
              template.template_kind === 'jurisdiction_variant'
                ? 'bg-lex-accent-soft text-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent'
                : template.template_kind === 'firm_variant'
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                  : 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-200'
            }`}>
              {template.template_kind.replace(/_/g, ' ')}
            </span>
          )}
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
              template.is_system_template
                ? 'bg-lex-accent-soft text-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
            }`}
          >
            {template.is_system_template ? 'System' : 'Firm Custom'}
          </span>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-700 dark:text-gray-300 md:grid-cols-4">
        <div>
          <span className="block text-gray-500 dark:text-gray-400">Practice Area</span>
          <span>{template.practice_area || 'Unspecified'}</span>
        </div>
        <div>
          <span className="block text-gray-500 dark:text-gray-400">Jurisdiction</span>
          <span>{formatJurisdictionLabel(template.jurisdiction)}</span>
        </div>
        <div>
          <span className="block text-gray-500 dark:text-gray-400">Tasks / Phases</span>
          <span>
            {template.task_count} / {template.phase_count}
          </span>
        </div>
        <div>
          <span className="block text-gray-500 dark:text-gray-400">Forms</span>
          <span>{template.form_count ?? 0} workflow</span>
          {template.catalog_form_count > 0 && template.catalog_form_count !== template.form_count && (
            <span className="ml-1 text-gray-400 dark:text-gray-500">/ {template.catalog_form_count} catalog</span>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-gray-300">
        <span className="inline-flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${confidenceColor(template.confidence_score)}`} />
          Confidence: {template.confidence_score || 'N/A'}
        </span>
        <span>Last Crawl: {formatDate(template.last_crawl_date)}</span>
        <span>Updated: {formatDate(template.updated_at)}</span>
        <span>Usage: {template.usage_count}</span>
        <span>Lifecycle: {template.lifecycle_status || 'draft'}</span>
        {!template.is_system_template && <span>Inherits Updates: {template.inherits_updates ? 'Yes' : 'No'}</span>}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => onViewDetails(template.id)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          View Details
        </button>
        <button
          onClick={() => (template.is_system_template ? onCreateVariant(template.id) : onEditTemplate(template.id))}
          disabled={template.is_system_template && isForking}
          className="rounded-md bg-lex-accent px-3 py-1.5 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          {template.is_system_template ? (isForking ? 'Creating Variant...' : 'Create Firm Variant') : 'Edit'}
        </button>
        {template.is_system_template && template.template_kind === 'jurisdiction_base' && (
          <button
            onClick={() => onEditTemplate(template.id)}
            className="rounded-md border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
          >
            Edit Base
          </button>
        )}
        {(!template.is_system_template || template.template_kind === 'jurisdiction_base') && onArchiveTemplate && (
          <button
            onClick={() => onArchiveTemplate(template)}
            className="flex items-center gap-1 rounded-md border border-amber-300 dark:border-amber-700 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors duration-150"
          >
            <ArchiveBoxIcon className="h-4 w-4" />
            Archive
          </button>
        )}
      </div>
    </div>
  );
};

export default TemplateCard;
